import { supabase } from '@/lib/supabase';

/** Columns known to exist on public.jphph_profiles — avoid optional/missing fields. */
export const PORTAL_PROFILE_SELECT =
  'id, full_name, email, user_type, company_name, phone, avatar_url, is_active, created_at, updated_at';

export interface PortalProfileRecord {
  id: string;
  full_name: string | null;
  email: string;
  user_type: string;
  company_name: string | null;
  phone: string | null;
  avatar_url: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/** Legacy Connect / cached profile keys — never use for sidebar display. */
const STALE_PROFILE_CACHE_KEY_FRAGMENTS = [
  'jph_profile_display',
  'jph_connect_profile',
  'jphc_profile',
  'connect_profile',
  'profile_display_name',
  'displayname',
  'username',
  'user_name',
];

export function clearStalePortalProfileCache(): void {
  if (typeof window === 'undefined') return;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key) continue;
      const lower = key.toLowerCase();
      if (STALE_PROFILE_CACHE_KEY_FRAGMENTS.some((fragment) => lower.includes(fragment))) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // localStorage unavailable
  }
}

export function normalizePortalEmail(email?: string | null): string | null {
  const trimmed = email?.trim().toLowerCase();
  return trimmed || null;
}

/**
 * Load the Portal profile from public.jphph_profiles only.
 * Email is preferred over auth user id because auth id can point at a stale/wrong row.
 */
export async function fetchPortalProfile(
  userId: string,
  userEmail?: string | null,
): Promise<{ profile: PortalProfileRecord | null; source: string; error?: string }> {
  const normalizedEmail = normalizePortalEmail(userEmail);

  if (normalizedEmail) {
    const { data: byEmail, error: emailErr } = await supabase
      .from('jphph_profiles')
      .select(PORTAL_PROFILE_SELECT)
      .eq('email', normalizedEmail)
      .maybeSingle();

    if (emailErr) {
      console.warn('[fetchPortalProfile] email query error:', emailErr.message);
    } else if (byEmail) {
      return { profile: byEmail as PortalProfileRecord, source: 'jphph_profiles.email' };
    }
  }

  const { data: byId, error: idErr } = await supabase
    .from('jphph_profiles')
    .select(PORTAL_PROFILE_SELECT)
    .eq('id', userId)
    .maybeSingle();

  if (idErr) {
    console.warn('[fetchPortalProfile] id query error:', idErr.message);
    return { profile: null, source: 'jphph_profiles.id', error: idErr.message };
  }

  if (byId) {
    if (normalizedEmail) {
      const rowEmail = normalizePortalEmail(byId.email);
      if (rowEmail && rowEmail !== normalizedEmail) {
        return { profile: null, source: 'jphph_profiles.id_email_mismatch' };
      }
    }
    return { profile: byId as PortalProfileRecord, source: 'jphph_profiles.id' };
  }

  return { profile: null, source: 'none' };
}
